/**
 * Pre-Publish Section Component
 *
 * Displays pre-publish validation status and allows running checks.
 * Shows when story is in AUDIO_READY phase with mixed audio.
 * Conditional buttons based on resolvability.
 *
 * @module PrePublishSection
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Play,
  RefreshCw,
} from 'lucide-react';
import { useNotify, useRefresh } from 'react-admin';
import type { StoryPrePublishCheckId } from '@musedrops/shared';
import { getCheckDefinition } from '@musedrops/shared';
import { runPrePublishChecks } from '../../../services/prePublishService';

interface PrePublishSectionProps {
  storyId: string;
  lang?: string; // Language for translation-specific checks
  phase: string;
  // Uses timestamp-based detection instead of file path
  mixedAudioUpdatedAt?: string | null;
  prePublishCheckedAt?: string | null;
  prePublishIssues?: StoryPrePublishCheckId[] | null;
}

export const PrePublishSection: React.FC<PrePublishSectionProps> = ({
  storyId,
  lang,
  phase,
  mixedAudioUpdatedAt,
  prePublishCheckedAt,
  prePublishIssues,
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const notify = useNotify();
  const refresh = useRefresh();

  // Show for AUDIO_READY and EXTERNAL_AUDIO_READY phases with mixed audio
  const isEligible =
    (phase === 'AUDIO_READY' || phase === 'EXTERNAL_AUDIO_READY') &&
    Boolean(mixedAudioUpdatedAt);

  // Also show for READY_FOR_PUBLISH to display success state or remaining issues
  const showSection = isEligible || phase === 'READY_FOR_PUBLISH';

  if (!showSection) {
    return null;
  }

  const handleRunChecks = async (force = false) => {
    setIsRunning(true);
    setError(null);

    try {
      // Pass force=true for manual retries (bypasses loop prevention)
      const result = await runPrePublishChecks({ storyId, lang, force });

      if (!result.success) {
        setError(result.error || 'Pre-publish check failed');
        notify('Pre-publish check failed', { type: 'error' });
        return;
      }

      // Handle different statuses
      switch (result.status) {
        case 'ready':
          notify('All checks passed! Story ready for publish.', {
            type: 'success',
          });
          break;
        case 'scheduled':
          notify('Story scheduled for publication!', { type: 'success' });
          break;
        case 'auto_fixing':
          notify(`Auto-fix triggered: ${result.issue}`, { type: 'info' });
          break;
        case 'blocked':
          notify(`${result.issues?.length || 0} issue(s) need attention`, {
            type: 'warning',
          });
          break;
        case 'no_progress':
          notify('Issues unchanged - manual fix required', { type: 'info' });
          break;
        case 'skipped':
          notify('Story not eligible for pre-publish', { type: 'info' });
          break;
        case 'reverted':
          notify('Story reverted - missing required audio', { type: 'warning' });
          break;
        default:
          break;
      }

      // Refresh to get updated story data
      setTimeout(() => refresh(), 500);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMsg);
      notify('Pre-publish check failed', { type: 'error' });
    } finally {
      setIsRunning(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const hasIssues = prePublishIssues && prePublishIssues.length > 0;
  // allPassed requires no issues AND checks have been run
  // Don't assume READY_FOR_PUBLISH means all passed - issues may have been added after phase transition
  const allPassed = prePublishCheckedAt && !hasIssues;

  // Determine if any issues require manual fix (highest severity = error)
  const hasManualIssues =
    hasIssues &&
    prePublishIssues.some(issueId => {
      const checkDef = getCheckDefinition(issueId);
      return !checkDef?.autoFixFunction;
    });

  // Determine if any issues are auto-resolvable (show "Check & Fix" button)
  const hasAutoResolvable =
    hasIssues &&
    prePublishIssues.some(issueId => {
      const checkDef = getCheckDefinition(issueId);
      return Boolean(checkDef?.autoFixFunction);
    });

  // Severity: error if manual issues exist, warning if only auto-fixable
  const severity: 'error' | 'warning' = hasManualIssues ? 'error' : 'warning';
  const severityColor = severity === 'error' ? '#d32f2f' : '#ed6c02';

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        ...(hasIssues && {
          borderTop: `5px solid ${severityColor}`,
        }),
        ...(allPassed && {
          borderTop: '5px solid #2e7d32',
        }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: hasIssues || allPassed || error ? 1 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            Pre-Publish Checks
            {allPassed && <CheckCircle size={16} color="#2e7d32" />}
          </Typography>
          {prePublishCheckedAt && (
            <Typography variant="caption" color="text.secondary">
              · {formatDate(prePublishCheckedAt)}
            </Typography>
          )}
        </Box>

        {showSection && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Show "Check & Fix" only when there are auto-resolvable issues */}
            {hasAutoResolvable && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleRunChecks(true)}
                disabled={isRunning}
                startIcon={
                  isRunning ? (
                    <CircularProgress size={14} />
                  ) : (
                    <Play size={14} />
                  )
                }
                sx={{ py: 0.25, px: 1, minHeight: 0 }}
              >
                {isRunning ? 'Checking...' : 'Check & Fix'}
              </Button>
            )}
            {/* Refresh icon always available for re-checking or re-validating */}
            <IconButton
              size="small"
              onClick={() => handleRunChecks(true)}
              disabled={isRunning}
              title={phase === 'READY_FOR_PUBLISH' ? 'Re-validate checks' : 'Re-check issues'}
              sx={{ p: 0.5 }}
            >
              {isRunning ? (
                <CircularProgress size={16} />
              ) : (
                <RefreshCw size={16} />
              )}
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Success state */}
      {allPassed && (
        <Typography variant="body2" color="success.main">
          {phase === 'READY_FOR_PUBLISH'
            ? 'All checks passed. Ready for publishing!'
            : 'All checks passed. Run again to advance to Ready.'}
        </Typography>
      )}

      {/* Issues list - compact inline format */}
      {hasIssues && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {prePublishIssues.map(issueId => {
            const checkDef = getCheckDefinition(issueId);
            const isAutoResolvable = Boolean(checkDef?.autoFixFunction);
            const message = checkDef?.message || issueId;

            return (
              <Box
                key={issueId}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                {isAutoResolvable ? (
                  <AlertTriangle size={14} color="#ed6c02" />
                ) : (
                  <XCircle size={14} color="#d32f2f" />
                )}
                <Typography variant="body2">
                  {isAutoResolvable ? message : `${message} (Fix manually)`}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Not yet checked */}
      {!prePublishCheckedAt && !hasIssues && isEligible && (
        <Typography variant="body2" color="text.secondary">
          Click the refresh icon to validate story for publishing.
        </Typography>
      )}

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};
